import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Copy } from 'lucide-react';
import DocEditor from './DocEditor';

/**
 * Google Slides–like editor: filmstrip + stage, rich text body via TipTap.
 */
export default function SlidesEditor({
  slides: initialSlides,
  editable = true,
  onChange,
}) {
  const [slides, setSlides] = useState(() => (
    initialSlides?.length ? initialSlides : [{ title: 'Diapositiva 1', body: '', notes: '', html: '<p></p>' }]
  ));
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (initialSlides?.length) {
      setSlides(initialSlides.map((s) => ({
        title: s.title || '',
        body: s.body || '',
        notes: s.notes || '',
        html: s.html || (s.body ? `<p>${String(s.body).replace(/\n/g, '</p><p>')}</p>` : '<p></p>'),
      })));
      setIdx(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSlides]);

  const emit = (next) => {
    setSlides(next);
    onChange?.(next.map((s) => ({
      title: s.title || '',
      body: stripHtml(s.html || s.body || ''),
      notes: s.notes || '',
      html: s.html || '',
    })));
  };

  const update = (field, val) => {
    const next = slides.map((s, i) => (i === idx ? { ...s, [field]: val } : s));
    emit(next);
  };

  const add = () => {
    const next = [...slides, {
      title: `Diapositiva ${slides.length + 1}`,
      body: '',
      notes: '',
      html: '<p></p>',
    }];
    emit(next);
    setIdx(next.length - 1);
  };

  const remove = () => {
    if (slides.length <= 1) return;
    const next = slides.filter((_, i) => i !== idx);
    emit(next);
    setIdx((i) => Math.max(0, Math.min(i, next.length - 1)));
  };

  const duplicate = () => {
    const cur = slides[idx];
    const next = [
      ...slides.slice(0, idx + 1),
      { ...cur, title: `${cur.title || 'Copia'} (copia)` },
      ...slides.slice(idx + 1),
    ];
    emit(next);
    setIdx(idx + 1);
  };

  const move = (dir) => {
    const j = idx + dir;
    if (j < 0 || j >= slides.length) return;
    const next = [...slides];
    [next[idx], next[j]] = [next[j], next[idx]];
    emit(next);
    setIdx(j);
  };

  const current = slides[idx] || slides[0];

  return (
    <div className="gslides-layout" data-testid="drive-slides-editor">
      <aside className="gslides-thumbs">
        {editable && (
          <div className="flex flex-wrap gap-1 mb-2 px-1">
            <button type="button" className="gdocs-tb-btn" onClick={add} title="Nueva"><Plus className="h-4 w-4" /></button>
            <button type="button" className="gdocs-tb-btn" onClick={duplicate} title="Duplicar"><Copy className="h-4 w-4" /></button>
            <button type="button" className="gdocs-tb-btn" onClick={() => move(-1)} title="Subir"><ChevronUp className="h-4 w-4" /></button>
            <button type="button" className="gdocs-tb-btn" onClick={() => move(1)} title="Bajar"><ChevronDown className="h-4 w-4" /></button>
            <button type="button" className="gdocs-tb-btn" onClick={remove} title="Eliminar"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
        {slides.map((s, i) => (
          <button
            key={i}
            type="button"
            className={`gslides-thumb ${i === idx ? 'active' : ''}`}
            onClick={() => setIdx(i)}
          >
            <span className="num">{i + 1}</span>
            <div className="mini">
              <strong>{s.title || 'Sin título'}</strong>
              <p>{stripHtml(s.html || s.body || '').slice(0, 80)}</p>
            </div>
          </button>
        ))}
      </aside>

      <div className="gslides-stage">
        <div className="gslides-slide gslides-slide--rich">
          {editable ? (
            <input
              className="gslides-title"
              value={current?.title || ''}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Haz clic para agregar título"
            />
          ) : (
            <h1 className="gslides-title">{current?.title}</h1>
          )}
          <div className="gslides-body-rich">
            <DocEditor
              key={`slide-${idx}-${slides.length}`}
              html={current?.html || '<p></p>'}
              editable={editable}
              variant="embedded"
              showToolbar={editable}
              onChange={(html) => update('html', html)}
            />
          </div>
        </div>
        {editable && (
          <textarea
            className="gslides-notes"
            value={current?.notes || ''}
            onChange={(e) => update('notes', e.target.value)}
            placeholder="Notas del orador…"
          />
        )}
        <p className="gdrive-hint">{idx + 1} / {slides.length}</p>
      </div>
    </div>
  );
}

function stripHtml(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}
